import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors"; 
import { toast } from "react-toastify";
import { 
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  useTheme,
  Paper,
  IconButton,
  Tooltip
} from "@mui/material";
import { 
  Campaign as CampaignIcon,
  AccountTree as FlowIcon,
  PhoneEnabled as PhoneEnabledIcon,
  TextFields as TextFieldsIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import Autocomplete from "@mui/material/Autocomplete";

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary
  },
  formGroup: {
    marginBottom: theme.spacing(2),
    "& > .MuiTypography-root": {
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      "& .MuiSvgIcon-root": {
        color: theme.palette.primary.main,
        transition: 'color 0.2s ease',
        "&:hover": {
          color: theme.palette.primary.dark
        }
      }
    }
  },
  textField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.2s ease',
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 2,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.dark,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.dark,
      }
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary
    }
  },
  select: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.2s ease',
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 2,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.dark,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.dark,
      }
    },
    "& .MuiSelect-select": {
      color: theme.palette.text.primary,
    }
  },
  statusOnline: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.success.light,
    fontSize: '0.875rem'
  },
  statusOffline: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.error.light,
    fontSize: '0.875rem'
  },
  buttonGroup: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderTop: `2px solid ${theme.palette.divider}`
  },
  dialogTitle: {
    padding: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "& h6": {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontWeight: 600,
      fontSize: '1.1rem',
      color: theme.palette.text.primary,
      "& .MuiSvgIcon-root": {
        color: theme.palette.primary.main
      }
    }
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      '& .MuiIconButton-root': {
        color: theme.palette.primary.dark
      }
    }
  },
  connectionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }
  }
}));

const CampaignModalPhrase = ({ open, onClose, FlowCampaignId, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  const [dataItem, setDataItem] = useState({
    name: "",
    phrase: "",
  });

  const [dataItemError, setDataItemError] = useState({
    name: false,
    flowId: false,
    phrase: false,
    whatsappId: false, 
  });

  const [flowSelected, setFlowSelected] = useState(null);
  const [flowsData, setFlowsData] = useState([]);
  const [flowsDataComplete, setFlowsDataComplete] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [whatsApps, setWhatsApps] = useState([]);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);

  const resetFieldsAndErrors = () => {
    setDataItem({
      name: "",
      phrase: "",
    });
    setFlowSelected(null);
    setSelectedWhatsapp("");
    setActive(true);
    setDataItemError({
      name: false,
      flowId: false,
      phrase: false,
      whatsappId: false,
    });
  };

  const getFlows = async () => {
    const flows = await api.get("/flowbuilder");
    setFlowsDataComplete(flows.data.flows);
    setFlowsData(flows.data.flows.map((flow) => flow.name));
    return flows.data.flows;
  };

  const detailsPhrase = async (flows) => {
    setLoading(true);
    await api.get(`/flowcampaign/${FlowCampaignId}`).then((res) => {
      setDataItem({
        name: res.data.details.name || "",
        phrase: res.data.details.phrase || "",
      });
      setActive(res.data.details.status);

      const nameFlow = flows.find(
        (itemFlows) => itemFlows.id === res.data.details.flowId
      );
      if (nameFlow) {
        setFlowSelected(nameFlow.name);
      }

      setSelectedWhatsapp(res.data.details.whatsappId || "");
      setLoading(false);
    });
  };

  const openModal = async () => {
    const flows = await getFlows();

    if (FlowCampaignId) {
      await detailsPhrase(flows);
    } else {
      resetFieldsAndErrors();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      openModal();
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    setDataItem((prev) => ({
      ...prev,
      [field]: value || "", 
    }));
  };

  const applicationSaveAndEdit = async () => {
    let error = 0;
    setDataItemError({
      name: false,
      flowId: false,
      phrase: false,
      whatsappId: false, 
    });

    if (dataItem.name.trim() === "") {
      setDataItemError((old) => ({ ...old, name: true }));
      error++;
    }
    if (!flowSelected) {
      setDataItemError((old) => ({ ...old, flowId: true }));
      error++;
    }
    if (dataItem.phrase.trim() === "") {
      setDataItemError((old) => ({ ...old, phrase: true }));
      error++;
    }
    if (!selectedWhatsapp) {
      setDataItemError((old) => ({ ...old, whatsappId: true }));
      error++;
    }

    if (error !== 0) {
      return;
    }

    const idFlow = flowsDataComplete.find((item) => item.name === flowSelected).id;
    const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;

    try {
      if (FlowCampaignId) {
        await api.put("/flowcampaign", {
          id: FlowCampaignId,
          name: dataItem.name,
          flowId: idFlow,
          whatsappId: whatsappId,
          phrase: dataItem.phrase,
          status: active,
        });
        toast.success("Frase alterada com sucesso!");
      } else {
        await api.post("/flowcampaign", {
          name: dataItem.name,
          flowId: idFlow,
          whatsappId: whatsappId,
          phrase: dataItem.phrase,
        });
        toast.success("Frase criada com sucesso!");
      }
      onSave();
      resetFieldsAndErrors();
      onClose();
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    const fetchWhatsApps = async () => {
      try {
        const { data } = await api.get(`/whatsapp`, {
          params: { companyId, session: 0 },
        });
        setWhatsApps(data);
      } catch (error) {
        toastError(error);
      }
    };
    fetchWhatsApps();
  }, [companyId]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetFieldsAndErrors();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 2,
          backgroundColor: theme.palette.background.default
        }
      }}
    >
      <Box className={classes.dialogTitle}>
        <Typography variant="h6">
          <CampaignIcon sx={{ color: theme.palette.primary.main }} />
          {FlowCampaignId
            ? "Editar campanha com fluxo por frase"
            : "Nova campanha com fluxo por frase"}
        </Typography>
      </Box>

      <Box className={classes.dialogContent}>
        {!loading ? (
          <Stack spacing={2}>
            <Box className={classes.formGroup}>
              <Typography>
                <CampaignIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                Nome da campanha
              </Typography>
              <TextField
                size="small"
                placeholder="Digite um nome descritivo para sua campanha"
                variant="outlined"
                error={dataItemError.name}
                value={dataItem.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={classes.textField}
                fullWidth
              />
              {dataItemError.name && (
                <FormHelperText error>
                  O nome da campanha é obrigatório
                </FormHelperText>
              )}
            </Box>

            <Box className={classes.formGroup}>
              <Typography>
                <FlowIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                Fluxo de mensagens
              </Typography>
              <Autocomplete
                size="small"
                value={flowSelected}
                options={flowsData}
                onChange={(event, newValue) => setFlowSelected(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Selecione o fluxo de mensagens"
                    error={dataItemError.flowId}
                    className={classes.textField}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-input': {
                    color: theme.palette.text.primary
                  }
                }}
              />
              {dataItemError.flowId && (
                <FormHelperText error>
                  O fluxo é obrigatório
                </FormHelperText>
              )}
            </Box>

            <Box className={classes.formGroup}>
              <Typography>
                <PhoneEnabledIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                Conexão WhatsApp
              </Typography>
              <Select
                size="small"
                value={selectedWhatsapp}
                onChange={(e) => setSelectedWhatsapp(e.target.value)}
                displayEmpty
                variant="outlined"
                className={classes.select}
                error={dataItemError.whatsappId}
                renderValue={() => {
                  if (!selectedWhatsapp) {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        Selecione uma conexão do WhatsApp
                      </Typography>
                    );
                  }
                  const whatsapp = whatsApps.find(
                    (w) => w.id === selectedWhatsapp
                  );
                  return (
                    <Box className={classes.connectionItem}>
                      <Typography variant="body2">{whatsapp.name}</Typography>
                      <Box
                        component="span"
                        className={
                          whatsapp.status === "CONNECTED"
                            ? classes.statusOnline
                            : classes.statusOffline
                        }
                      >
                        <PowerIcon fontSize="small" />
                        {whatsapp.status === "CONNECTED"
                          ? "Online"
                          : "Offline"}
                      </Box>
                    </Box>
                  );
                }}
              >
                {whatsApps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id} className={classes.menuItem}>
                    <Box className={classes.connectionItem}>
                      <Typography variant="body2">{whatsapp.name}</Typography>
                      <Box
                        component="span"
                        className={
                          whatsapp.status === "CONNECTED"
                            ? classes.statusOnline
                            : classes.statusOffline
                        }
                      >
                        <PowerIcon fontSize="small" />
                        {whatsapp.status === "CONNECTED"
                          ? "Online"
                          : "Offline"}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {dataItemError.whatsappId && (
                <FormHelperText error>
                  A conexão é obrigatória
                </FormHelperText>
              )}
            </Box>

            <Box className={classes.formGroup}>
              <Typography>
                <TextFieldsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                Frase ativadora
              </Typography>
              <TextField
                size="small"
                placeholder="Digite a frase que irá ativar este fluxo de mensagens"
                variant="outlined"
                error={dataItemError.phrase}
                value={dataItem.phrase}
                onChange={(e) => handleInputChange("phrase", e.target.value)}
                className={classes.textField}
                fullWidth
                multiline
                rows={3}
              />
              {dataItemError.phrase && (
                <FormHelperText error>
                  A frase é obrigatória
                </FormHelperText>
              )}
            </Box>

            <Paper className={classes.statusToggle} elevation={0}>
              <Typography variant="body2" color="textPrimary">
                Status da campanha
              </Typography>
              <Tooltip title={active ? "Campanha ativa" : "Campanha inativa"}>
                <IconButton
                  size="small"
                  color={active ? "primary" : "default"}
                  onClick={() => setActive((prev) => !prev)}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      color: theme.palette.primary.dark
                    }
                  }}
                >
                  <PowerIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="textSecondary">
                {active ? "Campanha está ativa" : "Campanha está inativa"}
              </Typography>
            </Paper>
          </Stack>
        ) : (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Box className={classes.buttonGroup}>
        <Button
          variant="outlined"
          onClick={() => {
            resetFieldsAndErrors();
            onClose();
          }}
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={applicationSaveAndEdit}
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
        >
          {FlowCampaignId ? "Salvar alterações" : "Criar campanha"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CampaignModalPhrase;